<template>
  <div class="hris-page">
    <div class="hris-page-inner">
    <div class="hris-page-header">
      <span class="hris-page-eyebrow">Leaves</span>
      <h1 class="hris-page-title">Leave Application Form</h1>
      <p class="hris-page-subtitle">Submit a new leave request.</p>
    </div>

    <div class="hris-form-narrow">
    <div class="hris-form-banner hris-form-banner-warning">
      For Halfday covered leave, please file separate application.
    </div>

    <div class="hris-grid" style="margin-bottom: 20px;" v-if="is_pto_enabled === true">
      <div class="hris-stat-card">
        <p class="hris-stat-label">Total Leave Credits</p>
        <p class="hris-stat-value">{{ pto_remaining }}</p>
      </div>
    </div>
    <div class="hris-grid" style="margin-bottom: 20px;" v-else-if="is_pto_enabled === false">
      <div class="hris-stat-card">
        <p class="hris-stat-label">VL Credits</p>
        <p class="hris-stat-value">{{ vl_remaining }}</p>
      </div>
      <div class="hris-stat-card">
        <p class="hris-stat-label">SL Credits</p>
        <p class="hris-stat-value">{{ sl_remaining }}</p>
      </div>
    </div>

    <q-form>
      <div class="hris-form-section">
        <div class="hris-form-section-title">Leave Details</div>
        <div class="hris-form-row">
          <div class="hris-form-field">
            <q-select v-model="form.leaveType" :options="leaveOptions" @input="resetDates()" outlined color="green" dense label="Leave Type" ></q-select>
          </div>
          <div class="hris-form-field">
            <q-input v-model="form.leaveReliever" outlined color="green" dense label="Reliever" ></q-input>
          </div>
        </div>
      </div>

      <div class="hris-form-section">
        <div class="hris-form-section-title">Inclusive Dates</div>
        <div class="hris-form-row">
          <div class="hris-form-field">
            <q-input v-model="dateFromInput" label="From" outlined color="green" readonly dense>
              <template v-slot:append>
                <q-icon name="fa fa-calendar-alt" class="cursor-pointer">
                  <q-popup-proxy ref="qDateFromProxy" transition-show="flip-down" transition-hide="flip-up">
                    <q-date v-model="form.selectedDateFrom" @input="$refs.qDateFromProxy.hide()" :options="startDate" today-btn color="green" minimal ></q-date>
                  </q-popup-proxy>
                </q-icon>
              </template>
            </q-input>
          </div>
          <div class="hris-form-field">
            <q-input v-model="dateToInput" label="To" outlined color="green" readonly dense>
              <template v-slot:append>
                <q-icon name="fa fa-calendar-alt" class="cursor-pointer">
                  <q-popup-proxy ref="qDateToProxy" transition-show="flip-down" transition-hide="flip-up">
                    <q-date v-model="form.selectedDateTo" @input="$refs.qDateToProxy.hide()" :options="endDate" today-btn color="green" minimal />
                  </q-popup-proxy>
                </q-icon>
              </template>
            </q-input>
          </div>
          <div class="hris-form-field">
            <q-input v-model="dateReturnInput" label="Return Date" outlined color="green" readonly dense>
              <template v-slot:append>
                <q-icon name="fa fa-calendar-alt" class="cursor-pointer">
                  <q-popup-proxy ref="qDateRetProxy" transition-show="flip-down" transition-hide="flip-up">
                    <q-date v-model="form.dateReturn" @input="$refs.qDateRetProxy.hide()" :options="returnDate" today-btn color="green" minimal />
                  </q-popup-proxy>
                </q-icon>
              </template>
            </q-input>
          </div>
        </div>
      </div>

      <div class="hris-form-section">
        <div class="hris-form-section-title">Coverage & Reason</div>
        <div class="hris-form-row">
          <div class="hris-form-field">
            <div class="hris-form-label">Leave Covered</div>
            <div class="row q-gutter-md">
              <q-radio v-model="form.leaveCovered" val="Whole Day" label="Whole Day" color="green" />
              <q-radio v-model="form.leaveCovered" val="Half Day" label="Half Day" color="green" />
            </div>
          </div>
          <div class="hris-form-field" style="flex-basis: 100%;">
            <q-input v-model="form.leaveReason" outlined color="green" maxlength="200" autogrow clearable dense label="Reason" />
          </div>
        </div>
      </div>

      <div class="hris-form-actions">
        <q-btn unelevated class="hris-q-primary" @click="confirmSubmit" :loading="loading" label="Submit Application" style="min-width: 200px;">
          <template v-slot:loading>
            <q-spinner color="green" class="on-left" />
            Sending...
          </template>
        </q-btn>
      </div>
    </q-form>
    </div>

    <q-dialog v-model="loadingModel" persistent>
      <div class="hris-loading" style="background: transparent; min-width: 200px;">
        <q-spinner color="green" size="3em" />
        <div>Submitting Application</div>
      </div>
    </q-dialog>
    </div>
  </div>
</template>
<script>
import axios from 'axios';
import { date } from 'quasar';
import { mapGetters } from 'vuex';
let today = new Date();
export default {
  name: 'LeaveApplication',
  data() {
    return {
      loading: false,
      loadingModel: false,
      vl_remaining: 0.00,
      sl_remaining: 0.00,
      remaining: 0, // for pto (combined vl and sl)
      is_pto_enabled: false,
      twoDaysAfter: date.formatDate(date.addToDate(today, { days: 2 }), 'YYYY/MM/DD'),
      twoMonthsBefore: date.formatDate(date.subtractFromDate(today, { months: 2 }), 'YYYY/MM/DD'),
      twoMonthsAfter: date.formatDate(date.addToDate(today, { months: 2 }), 'YYYY/MM/DD'),
      form: {
        leaveType: 'Vacation Leave',
        selectedDateFrom: null,
        selectedDateTo: null,
        leaveReliever: null,
        leaveCovered: 'Whole Day',
        dateReturn: null,
        leaveReason: null
      },
      leaveOptions: [
        'Emergency Leave',
        'Vacation Leave',
        'Sick Leave',
        'Maternity Leave',
        'Paternity Leave'
      ],
    }
  },
  computed: {
    ...mapGetters({
        userData: 'auth/getUser'
    }),
    hasPtoFeature() {
      return (this.userData?.features || []).includes('pto');
    },
    pto_remaining() {
      return (parseFloat(this.remaining || 0)).toFixed(2);
    },
    dateFromInput() { return this.form.selectedDateFrom ? date.formatDate(this.form.selectedDateFrom, 'MMMM DD, YYYY') : 'Select Date'; },
    dateToInput() { return this.form.selectedDateTo ? date.formatDate(this.form.selectedDateTo, 'MMMM DD, YYYY') : 'Select Date'; },
    dateReturnInput() { return this.form.dateReturn ? date.formatDate(this.form.dateReturn, 'MMMM DD, YYYY') : 'Select Date'; }
  },
  methods: {
    loadLeaves: function() {
      this.is_pto_enabled = this.hasPtoFeature;
      axios.create({
          baseURL: this.apiUrl,
          headers: { 'Authorization': `Bearer ${this.token}` }
        })
        .get('/leave/credits/retrieve', { params: { category: 'single', keyword: 'user' } })
        .then((res) => {
          let leaveCredits = res.data.data;
          this.vl_remaining = leaveCredits.vl_remaining;
          this.sl_remaining = leaveCredits.sl_remaining;
          this.remaining = leaveCredits.remaining
        });
    },
    resetDates() {
        this.form.selectedDateFrom = null;
        this.form.selectedDateTo = null;
        this.form.dateReturn = null;
    },
    startDate(val) {
        if (this.form.leaveType === 'Vacation Leave') return val >= this.twoDaysAfter;
        return val >= this.twoMonthsBefore && val <= this.twoMonthsAfter;
    },
    endDate(val) {
        if (!this.startDate(val)) return false;
        if (this.form.selectedDateFrom) return val >= this.form.selectedDateFrom;
        return true;
    },
    returnDate(val) {
        if (this.form.selectedDateTo) return val > this.form.selectedDateTo;
        return true;
    },
    confirmSubmit() {
        if (!this.form.selectedDateFrom || !this.form.selectedDateTo || !this.form.dateReturn || !this.form.leaveReason) {
            this.$q.notify({ color: 'red-6', message: 'Please fill all details.' });
            return;
        }
        
        let dateFrom = date.extractDate(this.form.selectedDateFrom, 'YYYY/MM/DD');
        let dateTo = date.extractDate(this.form.selectedDateTo, 'YYYY/MM/DD');
        let diff = date.getDateDiff(dateTo, dateFrom, 'days') + 1;
        let tots = this.form.leaveCovered === 'Half Day' ? diff / 2 : diff;
        
        let remaining = 0;
        if (this.is_pto_enabled && ['Sick Leave', 'Vacation Leave', 'Emergency Leave'].includes(this.form.leaveType)) remaining = parseFloat(this.vl_remaining);
        else if (this.form.leaveType === 'Sick Leave') remaining = parseFloat(this.sl_remaining);
        else if (['Vacation Leave', 'Emergency Leave'].includes(this.form.leaveType)) remaining = parseFloat(this.vl_remaining);
        else remaining = 100; // Special leaves bypass simple balance check for now

        if (tots > remaining) {
            this.$q.notify({ color: 'red-6', message: this.is_pto_enabled ? 'Insufficient Leave Credits.' : 'Insufficient Credits.' });
            return;
        }

        this.submitApplication();
    },
    submitApplication() {
        this.loading = true;
        this.loadingModel = true;
        let formData = new FormData();
        formData.append('form_data', JSON.stringify(this.form));
        axios.create({
            baseURL: this.apiUrl,
            headers: { 'Authorization': `Bearer ${this.token}` }
        }).post('/leave/application', formData).then(res => {
            if (res.data.status) {
                this.$q.notify({ color: 'green-7', message: res.data.message });
                this.loadLeaves();
                this.form.selectedDateFrom = null; this.form.selectedDateTo = null; this.form.dateReturn = null; this.form.leaveReason = null;
            } else {
                this.$q.notify({ color: 'red-6', message: res.data.message });
            }
        }).finally(() => {
            this.loading = false;
            this.loadingModel = false;
        });
    }
  },
  mounted() {
    this.is_pto_enabled = this.hasPtoFeature;
    this.loadLeaves();
  }
}
</script>
